import { useEffect, useLayoutEffect } from "react";
import { BODY_NO_SCROLL_CLASS } from "src/utils/constants";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Shared lock state across modals to allow multiple modals to lock body scroll without interfering with each other
let lockCount = 0;
let scrollYBeforeLock = 0;
const TOUCHMOVE_LISTENER_OPTIONS: AddEventListenerOptions = { passive: false };

function hasScrollableAncestor(node: HTMLElement | null): boolean {
  let el = node;

  while (el && el !== document.body) {
    const style = getComputedStyle(el);
    const canScrollY =
      el.scrollHeight > el.clientHeight &&
      style.overflowY !== "hidden" &&
      style.overflow !== "hidden";
    const canScrollX =
      el.scrollWidth > el.clientWidth &&
      style.overflowX !== "hidden" &&
      style.overflow !== "hidden";

    if (canScrollY || canScrollX) return true;
    el = el.parentElement;
  }

  return false;
}

function resolveTouchTarget(e: TouchEvent): HTMLElement | null {
  const rawTarget = e.target as HTMLElement | null;
  if (!rawTarget) return null;
  if (!document.body.contains(rawTarget) && e.touches[0]) {
    return document.elementFromPoint(
      e.touches[0].clientX,
      e.touches[0].clientY
    ) as HTMLElement | null;
  }

  return rawTarget;
}

// Prevent background scroll on touch devices
function preventBackgroundScroll(e: TouchEvent) {
  if (!document.body.classList.contains(BODY_NO_SCROLL_CLASS)) return;

  const target = resolveTouchTarget(e);
  if (!target || !hasScrollableAncestor(target)) {
    e.preventDefault();
  }
}

export function useLockBodyScroll(locked: boolean): void {
  useIsomorphicLayoutEffect(() => {
    if (!locked) return;

    if (lockCount === 0) {
      scrollYBeforeLock = window.scrollY;
      document.body.classList.add(BODY_NO_SCROLL_CLASS);
      document.addEventListener(
        "touchmove",
        preventBackgroundScroll,
        TOUCHMOVE_LISTENER_OPTIONS
      );
    }
    lockCount++;

    return () => {
      lockCount = Math.max(0, lockCount - 1);

      if (lockCount === 0) {
        document.removeEventListener("touchmove", preventBackgroundScroll);
        document.body.classList.remove(BODY_NO_SCROLL_CLASS);
        window.scrollTo(0, scrollYBeforeLock);
      }
    };
  }, [locked]);
}
